import { BaseEquipment, CardioOrResistance, Equipment } from 'fitbeat.models';
import { Exercise, ExerciseFormat } from '../exercise';
import { IfilterRuleStatus, WorkoutSelection } from './workoutselection';

export type FilterHelper = (
  nextExercise: Exercise,
  priorExercise: Exercise,
  cardioOrResistance?: CardioOrResistance,
) => IfilterRuleStatus;

export function isValidFormatForFiltering(
  exerciseFormat: ExerciseFormat,
): IfilterRuleStatus {
  if (
    Exercise.isHomeExerciseFormat(exerciseFormat) ||
    Exercise.isQuadFormat(exerciseFormat) ||
    Exercise.isStudioExerciseFormat(exerciseFormat)
  ) {
    return { isValid: true, message: 'supported format' };
  }
  return { isValid: false, message: 'Not a supported format' };
}

export function exerciseFilterElementsMatch(
  nextExercise: Exercise,
  priorExercise: Exercise,
  cardioOrResistance: CardioOrResistance,
): IfilterRuleStatus {
  const priorExerciseWeight = Exercise.getAdjustedEquipmentWeightLevel(priorExercise, cardioOrResistance);
  const nextExercisWeight = Exercise.getAdjustedEquipmentWeightLevel(nextExercise, cardioOrResistance);
  const exerciseHomeBenchSetupPositionsMatch =
    nextExercise.homeBenchSetupPosition &&
    priorExercise.homeBenchSetupPosition &&
    nextExercise.homeBenchSetupPosition.toString() ===
      priorExercise.homeBenchSetupPosition.toString();
  if (!exerciseHomeBenchSetupPositionsMatch) {
    return { isValid: false, message: 'Home bench position do not match' };
  }

  const exerciseHomeBenchSetupAnchorPointMatch =
    nextExercise.homeBenchSetupAnchorPoint &&
    priorExercise.homeBenchSetupAnchorPoint &&
    nextExercise.homeBenchSetupAnchorPoint.toString() ===
      priorExercise.homeBenchSetupAnchorPoint.toString();
  if (!exerciseHomeBenchSetupAnchorPointMatch) {
    return { isValid: false, message: 'Band anchor points do not match' };
  }

  const exerciseEquipmentMatch =
    nextExercise.equipment.toString() === priorExercise.equipment.toString();
  if (!exerciseEquipmentMatch) {
    return { isValid: false, message: 'Equipments do not match' };
  }

  const exerciseEquipmentWeightLevelMatch =
    nextExercisWeight === priorExerciseWeight;
  if (!exerciseEquipmentWeightLevelMatch) {
    return { isValid: false, message: 'Equipments Weights do not match' };
  }

  return { isValid: true, message: 'Exercise settings match' };
}

export function exerciseBenchSetupValidation(
  nextExercise: Exercise,
  priorExercise: Exercise,
): IfilterRuleStatus {
  if (!WorkoutSelection.exerciseHasBenchEquipment(priorExercise)) {
    return { isValid: false, message: 'Previous exercise not a bench exercise' };
  }

  if (!WorkoutSelection.exerciseHasBenchEquipment(nextExercise)) {
    return { isValid: false, message: 'Next exercise not a bench exercise' };
  }

  if (!WorkoutSelection.isMatchingBenchPosition(nextExercise, priorExercise)) {
    return { isValid: false, message: 'Bench positions do not match' };
  }

  return { isValid: true, message: 'Bench position match' };
}

export function canEliminateToBodyweightRule(
  nextExercise: Exercise,
  priorExercise: Exercise,
  ): IfilterRuleStatus {

  if (WorkoutSelection.exerciseHasBenchEquipment(nextExercise)) {
    const hasSameBenchSetup = exerciseBenchSetupValidation(nextExercise, priorExercise);
    if (!hasSameBenchSetup.isValid) {
      return hasSameBenchSetup;
    }
  }

  const hasLessOrEqualEquipment =
    nextExercise.equipment.length <= priorExercise.equipment.length;

  if (!hasLessOrEqualEquipment) {
    return { isValid: false, message: 'Equipment list has more equipment' };
  }

  if (!nextExercise.equipment.length) {
    return { isValid: false, message: 'Next exercise does not have equipment' };
  }

  if (nextExercise.equipment.indexOf(Equipment.Bodyweight) === -1) {
    return { isValid: false, message: 'Next exercise equipment does not have Bodyweight' };
  }

  return { isValid: true, message: 'Can change to a bodyweight exercise' };
}

export function canChangeToAnchorBandBenchRule(
  nextExercise: Exercise,
  priorExercise: Exercise,
): IfilterRuleStatus {
  if (!WorkoutSelection.exerciseHasBenchEquipment(nextExercise)) {
    return {
      isValid: true,
      message: 'Not a bench exercise, band anchor skipping rule',
    };
  }
  if (!WorkoutSelection.isBenchAndBandsExercise(nextExercise)) {
    return {
      isValid: true,
      message: 'Not a bench band exercise, band anchor skipping rule',
    };
  }

  if (WorkoutSelection.isBenchAndBandsExercise(nextExercise)) {
    const hasSameBenchSetup = hasValidMatchingEquipment(nextExercise, priorExercise);
    if (!hasSameBenchSetup.isValid) {
      return hasSameBenchSetup;
    }
  }

  if (!WorkoutSelection.isBenchAndBandsExercise(priorExercise)) {
    return {
      isValid: false,
      message: 'Previous exercise not bench and band exercise, can not change to bench band exercise',
    };
  }

  if (WorkoutSelection.isAnchorPointSupportingBand(priorExercise)) {
    if (!WorkoutSelection.isAnchorPointSupportingBand(nextExercise)) {
      return {
        isValid: false,
        message: 'Invalid change, exercise not a anchor band exercise',
      };
    }

    if (!WorkoutSelection.isMatchingBandsAnchorPoint(nextExercise, priorExercise)) {
      return {
        isValid: false,
        message: 'Can not switch to a bench band anchor point exercise',
      };
    }
  }

  return {
    isValid: true,
    message: 'Valid bench change',
  };
}

export function hasValidEquipmentElimination(
  nextExercise: Exercise,
  priorExercise: Exercise,
  cardioOrResistance: CardioOrResistance,
): IfilterRuleStatus {
  if (cardioOrResistance === CardioOrResistance.Balance) {
    // Disable equipment sinkhole reduction rule for balance workouts.
    return { isValid: true, message: 'Is valid equipment elimination' };
  }
  const priorExerciseWeight = Exercise.getAdjustedEquipmentWeightLevel(priorExercise, cardioOrResistance);
  const nextExerciseWeight = Exercise.getAdjustedEquipmentWeightLevel(nextExercise, cardioOrResistance);
  const hasSameEquipment = hasValidMatchingEquipment(
    nextExercise,
    priorExercise,
  );
  if (hasSameEquipment.isValid) {
    return hasSameEquipment;
  }

  if (WorkoutSelection.isAnchorPointSupportingBand(priorExercise)) {
    if (WorkoutSelection.isAnchorPointSupportingBand(nextExercise)) {
      if (!WorkoutSelection.isMatchingBandsAnchorPoint(priorExercise, nextExercise)) {
        return {isValid: false, message: 'Incorrect anchor change'};
      }
    }

    if (WorkoutSelection.isUniBandExercise(nextExercise)
      && priorExerciseWeight === nextExerciseWeight) {
      return {isValid: false, message: 'Cannot remove this band from anchor point'};
    }
  }

  if (nextExercise.equipment.length < priorExercise.equipment.length) {
    const containsExistingEquipment = priorExercise.equipment.find(
      (singleEquipment: Equipment) => {
        return singleEquipment === nextExercise.equipment[0];
      });
    if (containsExistingEquipment) {
      return { isValid: true, message: 'Is valid equipment elimination' };
    }
  }
  return { isValid: false, message: 'Not a valid equipment elimination' };
}

export function hasValidMatchingEquipment(
  nextExercise: Exercise,
  priorExercise: Exercise,
): IfilterRuleStatus {
  if (WorkoutSelection.exerciseHasBenchEquipment(nextExercise)) {
    const hasSameBenchSetup = exerciseBenchSetupValidation(nextExercise, priorExercise);
    if (!hasSameBenchSetup.isValid) {
      return hasSameBenchSetup;
    }
  }

  if (nextExercise.equipment.length === priorExercise.equipment.length) {
    if (
      nextExercise.equipment.sort().toString() === priorExercise.equipment.sort().toString()
    ) {
      return { isValid: true, message: 'Equipment match' };
    }
  }

  return { isValid: false, message: 'Equipment do not match' };
}

export function hasSameEquipmentWeight(
  nextExercise: Exercise,
  priorExercise: Exercise,
  cardioOrResistance: CardioOrResistance,
): IfilterRuleStatus {
  const hasSameBenchSetup = hasValidEquipmentElimination(nextExercise, priorExercise, cardioOrResistance);
  const priorExerciseWeight = Exercise.getAdjustedEquipmentWeightLevel(priorExercise, cardioOrResistance);
  const nextExerciseWeight = Exercise.getAdjustedEquipmentWeightLevel(nextExercise, cardioOrResistance);
  if (hasSameBenchSetup.isValid &&
    priorExerciseWeight &&
    nextExerciseWeight &&
    nextExerciseWeight === Equipment.Bodyweight
    ) {
      return {
        isValid: true,
        message: 'Not checking weight, switching to Bodyweight',
      };
    }

  if (
    BaseEquipment.containsHomeDumbbells(priorExercise.equipment) &&
    BaseEquipment.containsHomeDumbbells(priorExercise.equipment)
  ) {
    return {
      isValid: true,
      message: 'Not checking weight, switching from dumbbell to dumbbell',
    };
  }

  if (
    hasSameBenchSetup.isValid &&
    priorExerciseWeight &&
    nextExerciseWeight &&
    priorExerciseWeight === nextExerciseWeight
  ) {
    return { isValid: true, message: 'Equipment weights are equal' };
  }
  return { isValid: false, message: 'Equipment weights are not equal' };
}

export function hasValidEquipmentEliminationForSuperSet(
  nextExercise: Exercise,
  priorExercise: Exercise,
  cardioOrResistance: CardioOrResistance,
): IfilterRuleStatus {
  const priorExerciseWeight = Exercise.getAdjustedEquipmentWeightLevel(priorExercise, cardioOrResistance);
  const nextExerciseWeight = Exercise.getAdjustedEquipmentWeightLevel(nextExercise, cardioOrResistance);
  if (nextExercise.equipment.length > nextExercise.equipment.length) {
    return {
      isValid: false,
      message: 'Superset: not valid equipment elimination',
    };
  }

  const hasOnlyBodyWeight = Exercise.isBodyWeightExercise(priorExercise);
  if (
    hasOnlyBodyWeight &&
    nextExercise.equipment.toString() !== priorExercise.equipment.toString()
  ) {
    return { isValid: false, message: 'Superset: not valid bodyweight change' };
  }

  if (WorkoutSelection.exerciseHasBenchEquipment(nextExercise)) {
    const hasSameBenchSetup = exerciseBenchSetupValidation(nextExercise, priorExercise);
    if (!hasSameBenchSetup.isValid) {
      return hasSameBenchSetup;
    }
  }

  if (WorkoutSelection.isAnchorPointSupportingBand(priorExercise)) {
    if (WorkoutSelection.isAnchorPointSupportingBand(nextExercise)) {
      if (!WorkoutSelection.isMatchingBandsAnchorPoint(priorExercise, nextExercise)) {
        return {isValid: false, message: ' Incorrect anchor change'};
      }
    }

    if (WorkoutSelection.isUniBandExercise(nextExercise)
      && priorExerciseWeight === nextExerciseWeight) {
      return {isValid: false, message: 'Cannot remove this band from anchor point'};
    }
  }

  const containsExistingEquipment = priorExercise.equipment.find(
    (singleEquipment: Equipment) => {
      return singleEquipment === nextExercise.equipment[0];
    });

  if (containsExistingEquipment) {
    return {
      isValid: true,
      message: 'Superset: Is valid equipment elimination',
    };
  }

  const checkForNoEquipmentIncrease =
    nextExercise.equipment.length > priorExercise.equipment.length;
  if (checkForNoEquipmentIncrease) {
    return {
      isValid: true,
      message: 'Superset: Not a valid equipment elimination',
    };
  }

  return { isValid: true, message: 'Superset: Valid equipment elimination' };
}

export function canChangeToBenchRuleForSuperset(
  nextExercise: Exercise,
  priorExercise: Exercise,
  cardioOrResistance: CardioOrResistance,
): IfilterRuleStatus {
  const priorExerciseWeight = Exercise.getAdjustedEquipmentWeightLevel(priorExercise, cardioOrResistance);
  const nextExerciseWeight = Exercise.getAdjustedEquipmentWeightLevel(nextExercise, cardioOrResistance);
  if (!WorkoutSelection.exerciseHasBenchEquipment(nextExercise)) {
    return {
      isValid: true,
      message: 'Superset: not a bench exercise, band anchor skipping rule',
    };
  }
  if (!WorkoutSelection.isBenchAndBandsExercise(nextExercise)) {
    return {
      isValid: true,
      message: 'Superset: not a bench band exercise, band anchor skipping rule',
    };
  }

  if (WorkoutSelection.exerciseHasBenchEquipment(nextExercise)) {
    const hasSameBenchSetup = exerciseBenchSetupValidation(nextExercise, priorExercise);
    if (!hasSameBenchSetup.isValid) {
      return hasSameBenchSetup;
    }
  }

  if (!WorkoutSelection.isBenchAndBandsExercise(priorExercise)) {
    return {
      isValid: true,
      message: 'Superset: Previous exercise not bench and band exercise, skipping rule',
    };
  }

  if (WorkoutSelection.isAnchorPointSupportingBand(priorExercise)) {
    if (WorkoutSelection.isUniBandExercise(nextExercise)
      && !WorkoutSelection.isAnchorPointSupportingBand(nextExercise)
      && priorExerciseWeight === nextExerciseWeight) {
      return {
        isValid: false,
        message: 'Superset: Cannot remove this band from anchor point',
      };
    }

    if (WorkoutSelection.isUniBandExercise(nextExercise)
      && !WorkoutSelection.isMatchingBandsAnchorPoint(nextExercise, priorExercise)
      && priorExerciseWeight === nextExerciseWeight) {
      return {
        isValid: false,
        message: 'Superset: Can not switch to a bench band anchor point exercise',
      };
    }
  }

  return {
    isValid: true,
    message: 'Superset: valid bench change',
  };
}

export function hasSameEquipmentWeightForSuperset(
  nextExercise: Exercise,
  priorExercise: Exercise,
  cardioOrResistance: CardioOrResistance,
): IfilterRuleStatus {
  const priorExerciseWeight = Exercise.getAdjustedEquipmentWeightLevel(priorExercise, cardioOrResistance);
  const nextExerciseWeight = Exercise.getAdjustedEquipmentWeightLevel(nextExercise, cardioOrResistance);
  const equipmentEliminationValidationResults = hasValidEquipmentElimination(nextExercise,
    priorExercise, cardioOrResistance);
  if (!equipmentEliminationValidationResults.isValid) {
    return {
      isValid: true,
      message: 'Superset: not checking weight, different equipment used',
    };
  }

  if (!priorExerciseWeight) {
    return {
      isValid: false,
      message: 'Superset: previous exercise does not have a valid equipment weight',
    };
  }

  if (!nextExerciseWeight) {
    return {
      isValid: false,
      message: 'Superset: next exercise does not have a valid equipment weight',
    };
  }

  if (
    nextExerciseWeight === Equipment.Bodyweight
    ) {
    return {
      isValid: true,
      message: 'Superset: not checking weight, switching to Bodyweight',
    };
  }

  if (
    BaseEquipment.containsHomeDumbbells(priorExercise.equipment) &&
    BaseEquipment.containsHomeDumbbells(priorExercise.equipment)
  ) {
    return {
      isValid: true,
      message: 'Superset: not checking weight, switching from dumbbell to dumbbell',
    };
  }

  if (
      WorkoutSelection.isUniBandExercise(priorExercise)
      && WorkoutSelection.isUniBandExercise(nextExercise)
      && WorkoutSelection.isAnchorPointSupportingBand(priorExercise)
      && !WorkoutSelection.isAnchorPointSupportingBand(nextExercise)
    ) {
      return { isValid: true, message: 'Superset: not checking weight, previous uniband was anchored' };
  }

  if (
    priorExerciseWeight === nextExerciseWeight
  ) {
    return { isValid: true, message: 'Superset: Equipment weights are equal' };
  }

  return {
    isValid: false,
    message: 'Superset: Equipment weights are not equal',
  };
}
